import React, { useEffect, useRef, useState } from 'react';
import mapboxgl from 'mapbox-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import { config } from '../config';

interface Stop {
  stopId: string;
  publicCode: string;
  name: string;
  arrivalOffset: number;
  departureOffset: number;
  coordinates: [number, number];
  routes: string[];
}

interface Pattern {
  id: string;
  headsign: string;
  direction: string;
  stops: Stop[];
  coordinates: [number, number][];
}

interface Route {
  id: string;
  type: string;
  shortName: string;
  longName: string;
  service: string;
  color: string;
  textColor: string;
  patterns: Pattern[];
}

interface RoutesResponse {
  routes: Route[];
}

const MapView: React.FC = () => {
  const containerRef = useRef<HTMLDivElement>(null);
  const mapContainer = useRef<HTMLDivElement>(null);
  const map = useRef<mapboxgl.Map | null>(null);
  const markersRef = useRef<mapboxgl.Marker[]>([]);
  const [routes, setRoutes] = useState<Route[]>([]);
  const [mapReady, setMapReady] = useState(false);
  const [selectedRoute, setSelectedRoute] = useState<Route | null>(null);
  const [selectedStop, setSelectedStop] = useState<Stop | null>(null);
  const hasFetchedRoutes = useRef(false);
  const clickHandlersAdded = useRef(false);
  const [isMobile, setIsMobile] = useState(false);

  // Detect mobile screen size
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Detect when component becomes visible and resize map
  useEffect(() => {
    if (!containerRef.current) return;

    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.type === 'attributes' && mutation.attributeName === 'style') {
          const element = mutation.target as HTMLElement;
          if (element.style.display !== 'none' && map.current) {
            console.log('MapView became visible, resizing map');
            setTimeout(() => {
              map.current?.resize();
            }, 50);
          }
        }
      });
    });

    const parent = containerRef.current.parentElement;
    if (parent) {
      observer.observe(parent, {
        attributes: true,
        attributeFilter: ['style']
      });
    }

    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    // Skip fetch if routes already loaded (hot reload)
    if (hasFetchedRoutes.current && routes.length > 0) {
      console.log('Skipping routes fetch - already loaded');
      return;
    }

    const fetchRoutes = async () => {
      try {
        const response = await fetch(config.services.RTA.routes, {
          headers: {
            'X-Organization-Id': 'c3b8f441-7a4b-46a9-b582-2c7ac3cb6f3f'
          }
        });
        const data: RoutesResponse = await response.json();
        setRoutes(data.routes);
        hasFetchedRoutes.current = true;
      } catch (error) {
        console.error('Error fetching routes:', error);
      }
    };

    fetchRoutes();
  }, []);

  useEffect(() => {
    const initializeMap = () => {
      if (!mapContainer.current) {
        return false;
      }

      if (map.current) {
        map.current.resize();
        return true;
      }

      const width = mapContainer.current.offsetWidth;
      const height = mapContainer.current.offsetHeight;

      if (width === 0 || height === 0) {
        return false;
      }

      mapboxgl.accessToken = config.map.apiKey;

      try {
        map.current = new mapboxgl.Map({
          container: mapContainer.current,
          style: 'mapbox://styles/mapbox/streets-v12',
          center: config.map.center,
          zoom: config.map.zoom,
          trackResize: true
        });

        map.current.on('load', () => {
          setMapReady(true);
          setTimeout(() => {
            map.current?.resize();
          }, 100);
        });

        map.current.on('styledata', () => {
          setTimeout(() => {
            map.current?.resize();
          }, 100);
        });

        map.current.on('error', (e) => {
          console.error('Map error:', e.error);
        });

        return true;
      } catch (error) {
        console.error('Error creating map:', error);
        return false;
      }
    };

    const attemptInit = () => {
      if (!initializeMap()) {
        retryTimeout = setTimeout(attemptInit, 100);
      }
    };

    let retryTimeout: NodeJS.Timeout;
    attemptInit();

    return () => {
      if (retryTimeout) {
        clearTimeout(retryTimeout);
      }
    };
  }, []);

  // Add route layers
  useEffect(() => {
    if (!map.current || !mapReady || routes.length === 0) return;

    // Check if style is actually loaded
    if (!map.current.isStyleLoaded()) {
      console.log('Style not loaded yet, waiting...');
      return;
    }

    try {
      routes.forEach((route) => {
        route.patterns.forEach((pattern, patternIndex) => {
          const sourceId = `route-${route.id}-pattern-${patternIndex}`;
          const layerId = `route-${route.id}-pattern-${patternIndex}-layer`;

          if (map.current?.getSource(sourceId)) return;

          map.current?.addSource(sourceId, {
            type: 'geojson',
            data: {
              type: 'Feature',
              properties: {
                name: route.longName,
                shortName: route.shortName,
                routeId: route.id,
                color: route.color,
                textColor: route.textColor,
                service: route.service,
                type: route.type
              },
              geometry: {
                type: 'LineString',
                coordinates: pattern.coordinates
              }
            }
          });

          map.current?.addLayer({
            id: layerId,
            type: 'line',
            source: sourceId,
            layout: {
              'line-join': 'round',
              'line-cap': 'round'
            },
            paint: {
              'line-color': `#${route.color}`,
              'line-width': 3,
              'line-opacity': 0.8
            }
          });
        });
      });

      // Add click handlers once
      if (!clickHandlersAdded.current && map.current) {
        routes.forEach((route) => {
          route.patterns.forEach((pattern, patternIndex) => {
            const layerId = `route-${route.id}-pattern-${patternIndex}-layer`;

            map.current?.on('click', layerId, () => {
              setSelectedRoute(route);
              setSelectedStop(null);
            });

            // Change cursor on hover
            map.current?.on('mouseenter', layerId, () => {
              if (map.current) {
                map.current.getCanvas().style.cursor = 'pointer';
              }
            });

            map.current?.on('mouseleave', layerId, () => {
              if (map.current) {
                map.current.getCanvas().style.cursor = '';
              }
            });
          });
        });
        clickHandlersAdded.current = true;
      }
    } catch (error) {
      console.error('Error adding route layers:', error);
    }
  }, [routes, mapReady]);

  // Handle route selection - show/hide routes and add stop markers
  useEffect(() => {
    if (!map.current || !mapReady || routes.length === 0) return;

    // Clear existing markers
    markersRef.current.forEach(marker => marker.remove());
    markersRef.current = [];

    if (selectedRoute) {
      // Hide all routes except selected
      routes.forEach((route) => {
        route.patterns.forEach((_, patternIndex) => {
          const layerId = `route-${route.id}-pattern-${patternIndex}-layer`;
          if (map.current?.getLayer(layerId)) {
            if (route.id === selectedRoute.id) {
              map.current.setLayoutProperty(layerId, 'visibility', 'visible');
              // Make selected route more prominent
              map.current.setPaintProperty(layerId, 'line-width', 5);
              map.current.setPaintProperty(layerId, 'line-opacity', 1);
            } else {
              map.current.setLayoutProperty(layerId, 'visibility', 'none');
            }
          }
        });
      });

      // Add stop markers for selected route
      const addedStopIds = new Set<string>();
      selectedRoute.patterns.forEach((pattern) => {
        pattern.stops.forEach((stop) => {
          // Avoid duplicate markers for same stop
          if (addedStopIds.has(stop.stopId)) return;
          addedStopIds.add(stop.stopId);

          // Create marker element
          const el = document.createElement('div');
          el.style.width = '20px';
          el.style.height = '20px';
          el.style.backgroundColor = `#${selectedRoute.color}`;
          el.style.borderRadius = '50%';
          el.style.border = '3px solid white';
          el.style.boxShadow = '0 2px 6px rgba(0,0,0,0.3)';
          el.style.cursor = 'pointer';

          const marker = new mapboxgl.Marker(el)
            .setLngLat(stop.coordinates)
            .addTo(map.current!);

          el.addEventListener('click', () => {
            setSelectedStop(stop);
          });

          markersRef.current.push(marker);
        });
      });

      // Fit map to selected route bounds
      const allCoords: [number, number][] = [];
      selectedRoute.patterns.forEach(pattern => {
        allCoords.push(...pattern.coordinates);
      });

      if (allCoords.length > 0) {
        const bounds = allCoords.reduce(
          (bounds, coord) => bounds.extend(coord as mapboxgl.LngLatLike),
          new mapboxgl.LngLatBounds(allCoords[0], allCoords[0])
        );

        map.current.fitBounds(bounds, {
          padding: isMobile
            ? { top: 50, bottom: 250, left: 20, right: 20 }
            : { top: 50, bottom: 50, left: 450, right: 50 },
          duration: 500
        });
      }
    } else {
      // Show all routes
      routes.forEach((route) => {
        route.patterns.forEach((_, patternIndex) => {
          const layerId = `route-${route.id}-pattern-${patternIndex}-layer`;
          if (map.current?.getLayer(layerId)) {
            map.current.setLayoutProperty(layerId, 'visibility', 'visible');
            map.current.setPaintProperty(layerId, 'line-width', 3);
            map.current.setPaintProperty(layerId, 'line-opacity', 0.8);
          }
        });
      });

      // Reset map view
      map.current.flyTo({
        center: config.map.center,
        zoom: config.map.zoom,
        duration: 500
      });
    }
  }, [selectedRoute, routes, mapReady, isMobile]);

  const handleBackToRoute = () => {
    setSelectedStop(null);
  };

  const handleClose = () => {
    setSelectedRoute(null);
    setSelectedStop(null);
  };

  return (
    <div ref={containerRef} style={{ width: '100%', height: '100vh', position: 'relative' }}>
      <div ref={mapContainer} style={{ width: '100%', height: '100%' }} />

      {selectedRoute && (
        <div style={isMobile ? styles.detailsPanelMobile : styles.detailsPanel}>
          <button
            onClick={handleClose}
            style={styles.closeButton}
          >
            ✕
          </button>

          {selectedStop ? (
            // Stop details view
            <div style={styles.detailsContent}>
              <div style={{
                ...styles.routeHeader,
                backgroundColor: `#${selectedRoute.color}`
              }}>
                <div style={styles.stopName}>{selectedStop.name}</div>
                <div style={styles.stopCode}>Stop #{selectedStop.publicCode}</div>
              </div>

              <div style={styles.detailsBody}>
                <button onClick={handleBackToRoute} style={styles.backButton}>
                  ← Back to Route
                </button>

                <div style={styles.detailRow}>
                  <span style={styles.detailLabel}>Stop ID:</span>
                  <span style={styles.detailValue}>{selectedStop.stopId}</span>
                </div>

                <div style={styles.detailRow}>
                  <span style={styles.detailLabel}>Coordinates:</span>
                  <span style={styles.detailValue}>
                    {selectedStop.coordinates[1].toFixed(4)}, {selectedStop.coordinates[0].toFixed(4)}
                  </span>
                </div>

                {selectedStop.routes && selectedStop.routes.length > 0 && (
                  <div style={styles.detailRow}>
                    <span style={styles.detailLabel}>Routes:</span>
                    <span style={styles.detailValue}>{selectedStop.routes.join(', ')}</span>
                  </div>
                )}

                <div style={styles.stopsSection}>
                  <h3 style={styles.stopsHeader}>On Route {selectedRoute.shortName}</h3>
                  <div style={styles.routeInfo}>
                    <div style={styles.routeInfoName}>{selectedRoute.longName}</div>
                    <div style={styles.routeInfoDetail}>Service: {selectedRoute.service}</div>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            // Route details view
            <div style={styles.detailsContent}>
              <div style={{
                ...styles.routeHeader,
                backgroundColor: `#${selectedRoute.color}`
              }}>
                <div style={styles.routeNumber}>{selectedRoute.shortName}</div>
                <div style={styles.routeName}>{selectedRoute.longName}</div>
              </div>

              <div style={styles.detailsBody}>
                <div style={styles.detailRow}>
                  <span style={styles.detailLabel}>Route ID:</span>
                  <span style={styles.detailValue}>{selectedRoute.id}</span>
                </div>

                <div style={styles.detailRow}>
                  <span style={styles.detailLabel}>Service:</span>
                  <span style={styles.detailValue}>{selectedRoute.service}</span>
                </div>

                <div style={styles.detailRow}>
                  <span style={styles.detailLabel}>Type:</span>
                  <span style={styles.detailValue}>{selectedRoute.type}</span>
                </div>

                <div style={styles.detailRow}>
                  <span style={styles.detailLabel}>Patterns:</span>
                  <span style={styles.detailValue}>{selectedRoute.patterns.length}</span>
                </div>

                <div style={styles.stopsSection}>
                  <h3 style={styles.stopsHeader}>Stops</h3>
                  <div style={styles.stopsList}>
                    {(() => {
                      const uniqueStops = new Map<string, Stop>();
                      selectedRoute.patterns.forEach(pattern => {
                        pattern.stops.forEach(stop => {
                          if (!uniqueStops.has(stop.stopId)) {
                            uniqueStops.set(stop.stopId, stop);
                          }
                        });
                      });
                      return Array.from(uniqueStops.values()).map(stop => (
                        <div
                          key={stop.stopId}
                          style={styles.stopItem}
                          onClick={() => setSelectedStop(stop)}
                        >
                          <div style={styles.stopItemName}>{stop.name}</div>
                          <div style={styles.stopItemCode}>#{stop.publicCode}</div>
                        </div>
                      ));
                    })()}
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const styles: { [key: string]: React.CSSProperties } = {
  detailsPanel: {
    position: 'absolute',
    top: '20px',
    left: '20px',
    width: '400px',
    maxHeight: 'calc(100vh - 40px)',
    backgroundColor: 'white',
    borderRadius: '16px',
    boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)',
    overflow: 'hidden',
    zIndex: 1000,
    display: 'flex',
    flexDirection: 'column'
  },
  detailsPanelMobile: {
    position: 'absolute',
    bottom: '0',
    left: '0',
    right: '0',
    width: '100%',
    maxHeight: '60vh',
    backgroundColor: 'white',
    borderTopLeftRadius: '16px',
    borderTopRightRadius: '16px',
    boxShadow: '0 -4px 32px rgba(0, 0, 0, 0.3)',
    overflow: 'hidden',
    zIndex: 1000,
    display: 'flex',
    flexDirection: 'column'
  },
  closeButton: {
    position: 'absolute',
    top: '16px',
    right: '16px',
    background: 'rgba(0, 0, 0, 0.6)',
    border: 'none',
    color: 'white',
    fontSize: '24px',
    width: '36px',
    height: '36px',
    borderRadius: '50%',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 10,
    fontWeight: 'bold',
    lineHeight: '1'
  },
  detailsContent: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    overflow: 'hidden'
  },
  routeHeader: {
    padding: '30px 24px',
    color: 'white',
    textAlign: 'center'
  },
  routeNumber: {
    fontSize: '48px',
    fontWeight: '900',
    marginBottom: '8px',
    textShadow: '2px 2px 4px rgba(0, 0, 0, 0.3)'
  },
  routeName: {
    fontSize: '20px',
    fontWeight: '600',
    textShadow: '1px 1px 2px rgba(0, 0, 0, 0.3)'
  },
  stopName: {
    fontSize: '24px',
    fontWeight: '700',
    marginBottom: '8px',
    textShadow: '1px 1px 2px rgba(0, 0, 0, 0.3)'
  },
  stopCode: {
    fontSize: '16px',
    fontWeight: '500',
    opacity: 0.9
  },
  detailsBody: {
    padding: '24px',
    overflowY: 'auto',
    flex: 1
  },
  backButton: {
    background: 'none',
    border: '2px solid #860038',
    color: '#860038',
    padding: '8px 16px',
    borderRadius: '8px',
    cursor: 'pointer',
    fontSize: '14px',
    fontWeight: '600',
    marginBottom: '16px',
    width: '100%'
  },
  detailRow: {
    display: 'flex',
    justifyContent: 'space-between',
    padding: '12px 0',
    borderBottom: '1px solid #e0e0e0'
  },
  detailLabel: {
    fontWeight: '600',
    color: '#666'
  },
  detailValue: {
    color: '#000',
    fontWeight: '500',
    textAlign: 'right',
    flex: 1,
    marginLeft: '16px'
  },
  stopsSection: {
    marginTop: '24px'
  },
  stopsHeader: {
    fontSize: '18px',
    fontWeight: '700',
    marginBottom: '16px',
    color: '#000'
  },
  stopsList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
    maxHeight: '300px',
    overflowY: 'auto'
  },
  stopItem: {
    backgroundColor: '#f5f5f5',
    padding: '12px 16px',
    borderRadius: '8px',
    cursor: 'pointer',
    border: '2px solid transparent',
    transition: 'border-color 0.2s'
  },
  stopItemName: {
    fontSize: '14px',
    fontWeight: '600',
    color: '#000',
    marginBottom: '4px'
  },
  stopItemCode: {
    fontSize: '12px',
    color: '#666'
  },
  routeInfo: {
    backgroundColor: '#f5f5f5',
    padding: '16px',
    borderRadius: '8px'
  },
  routeInfoName: {
    fontSize: '16px',
    fontWeight: '600',
    color: '#000',
    marginBottom: '8px'
  },
  routeInfoDetail: {
    fontSize: '14px',
    color: '#666'
  }
};

export default MapView;
